import LoginApi from '@/api-interface/login'
import AccountApi from '@/api-interface/user-account'
import { isEmpty, cloneDeep } from 'lodash-es'
import { format as formatDate, addSeconds, isFuture, differenceInMilliseconds } from 'date-fns'
import HTTP from '@/api-interface/http'
import RememberMe from '@/helpers/remember-me'

const defaultTokenValues = {
  idToken: '',
  accessToken: '',
  refreshToken: '',
  expiresIn: 0,
  expiresAt: ''
}

const cachedTokens = JSON.parse(window.sessionStorage.getItem('tokens'))
HTTP.authToken = cachedTokens ? cachedTokens.idToken : ''
export default {
  namespaced: true,

  state: {
    tokens: cachedTokens || defaultTokenValues,
    currentUserInfo: {
      accountKey: 0,
      accountFirstName: '',
      accountLastName: '',
      accountEmailAddress: '',
      accountPhones: [
        {
          accountPhoneDigits: '123123123',
          accountPhonePrimaryFlag: true,
          accountPhoneType: 'cell'
        }
      ],
      company: {},
      carrier: {},
      factoringProvider: {},
      permissions: {}
    },
    refreshTimerId: undefined
  },

  getters: {
    accountInfo(state, getters, rootState) {
      let companyInfo = cloneDeep(rootState['company'].currentCompanyInfo)
      companyInfo.billing = state.currentUserInfo.company ? state.currentUserInfo.company.billing : {}

      if (!companyInfo.companyKey) {
        // TODO: might need to merge both company info stuff together
        companyInfo = state.currentUserInfo.company
      }
      return { ...state.currentUserInfo, company: companyInfo }
    },

    isFactoring(state) {
      return state.currentUserInfo.accountType === 'factoring'
    },

    isShipper(state) {
      return state.currentUserInfo.accountType === 'shipper'
    },

    isCompanyAdmin(state) {
      return (state.currentUserInfo.permissions || {}).accountCompanyRoleAdminFlag
    },

    isCarrier(state) {
      return state.currentUserInfo.accountType === 'carrier'
    },

    isTrucklAdmin(state) {
      return state.currentUserInfo.accountType === 'truckl-admin'
    },

    isLoggedIn(state) {
      return !isEmpty(state.tokens.accessToken)
    },
    isTokenValid(state) {
      return isFuture(state.tokens.expiresAt)
    }
  },

  mutations: {
    SET_USER_INFO(state, userInfo) {
      state.currentUserInfo = Object.assign({}, userInfo)
    },

    SET_TOKEN_INFO(state, tokenInfo) {
      state.tokens = Object.assign({}, state.tokens, tokenInfo)
      state.tokens.expiresAt = addSeconds(new Date(), tokenInfo.expiresIn)
      window.sessionStorage.setItem('tokens', JSON.stringify(state.tokens))
      HTTP.authToken = tokenInfo.idToken
    },

    SET_REFRESH_TIMER_ID(state, timerId) {
      state.refreshTimerId = timerId
    },

    LOGOUT() {
      window.sessionStorage.clear()
      window.localStorage.clear()
      // doing a "hard redirect" will reset all state stuff to their default values and remove any event listeners.
      window.location = '/'
    }
  },

  actions: {
    async updateDetails({ dispatch }, userDetails) {
      await AccountApi.update(userDetails)
      // because the Java services do not return anything related to user permissions, we need to call the other API
      // to to get the user info with permissions included.
      await dispatch('getMyDetails')
    },

    async getMyDetails({ commit }) {
      const response = await AccountApi.getDetailsByAccountKey('me')
      commit('SET_USER_INFO', response)
    },

    async getDetails({ commit }, email) {
      const response = await AccountApi.getDetailsByEmail(email)
      commit('SET_USER_INFO', response)
    },

    /**
     * Authenticates a user based on the given credentials and set tokens
     *
     * @param commit
     * @param dispatch
     * @param {string} username
     * @param {string} password
     * @returns {Promise<void>}
     */
    async authenticate({ commit, dispatch, getters }, { username, password, keepLoggedIn }) {
      const response = await LoginApi.login({ username, password })
      commit('SET_TOKEN_INFO', response)
      dispatch('startRefreshTimer')
      // save user creds to session storage so we can user it in the future to fetch fresh tokens
      // TODO: is there a way to use refresh tokens with Cognito?
      const creds = `${username}::${password}`
      window.sessionStorage.setItem('creds', window.btoa(creds))

      if (keepLoggedIn) {
        RememberMe.storeCreds({ username, password })
      }

      await dispatch('getMyDetails', username)
      if (getters.isShipper) {
        commit('SET_FILTER_BY_USER', getters.accountInfo.accountKey, { root: true })
      }
    },

    /**
     * Starts a clock to refresh all tokens before they expire
     *
     * @param commit
     * @param dispatch
     * @param state
     */
    async startRefreshTimer({ commit, dispatch, state }) {
      // stop the old timer to make sure we don't have multiple refresh timers running at once
      window.clearTimeout(state.refreshTimerId)

      // schedule the token refresh 60 seconds before the actual expiration
      const TIME_LEFT = differenceInMilliseconds(state.tokens.expiresAt, new Date()) - 60

      const refreshTime = formatDate(state.tokens.expiresAt, 'hh:mm:ss A')
      console.info(`Next token refresh is in ${TIME_LEFT}ms or at ${refreshTime}`)

      if (!TIME_LEFT) {
        // if the time has already ran out... grab a new token right now
        await dispatch('refreshToken')
      }

      const timerId = window.setTimeout(() => {
        dispatch('refreshToken')
      }, TIME_LEFT)

      commit('SET_REFRESH_TIMER_ID', timerId)
    },

    /**
     * Retrieves a new token and starts a new timer
     *
     * @param commit
     * @param dispatch
     * @param getters
     * @returns {Promise<void>}
     */
    /**
     * Retrieves a new token and starts a new timer.
     * Will redirect to the log-in screen if the user is not already logged in (aka: had a previous token)
     *
     * @param commit
     * @param dispatch
     * @param getters
     * @returns {Promise<void>}
     */
    async refreshToken({ commit, dispatch, getters }) {
      if (!getters.isLoggedIn) {
        window.alert('It looks like you are no longer logged in. \nPlease log back in to continue using Truckl')
        window.location = '/'
      }

      try {
        const [username, password] = window.atob(window.sessionStorage.getItem('creds')).split('::')
        const newToken = await LoginApi.refreshToken({ username, password })
        commit('SET_TOKEN_INFO', newToken)
        dispatch('startRefreshTimer')
      } catch (error) {
        console.error(error)
      }
    },

    /**
     * Ends the users session and invalidates tokens
     *
     * @param commit
     * @returns {Promise<void>}
     */
    async invalidate({ commit }) {
      await LoginApi.logout()
      commit('LOGOUT')
    }
  }
}
